import React, { useState, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { useCreateFact } from '@/api/mutations/useCreateFact';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { ClassificationOptions, RelevanceOptions } from '@/constants/filterOptions';
import { useDebounce } from '@/new/components/apideck/ApideckFilePickerCustom/utils/useDebounce';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/new/components/shadcn/command';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Textarea } from '@/new/components/shadcn/textarea';
import { cn } from '@/new/lib/utils';
import { BoundingBox, Classification, MyOptionType, Relevance } from '@/types';

import 'react-calendar/dist/Calendar.css';

interface FactAdderProps {
  handleNewFactCreated: () => void;
  docId?: string;
  sourceText?: string;
  highlightAreas?: BoundingBox[];
}

export function FactAdder({ handleNewFactCreated, docId, highlightAreas, sourceText = '' }: FactAdderProps) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const [selectedDoc, setSelectedDoc] = useState<MyOptionType>(null);
  const [selectedRelevance, setSelectedRelevance] = useState<MyOptionType>({
    label: 'Yes',
    value: Relevance.Relevant,
  });
  const [selectedClassification, setSelectedClassification] = useState<MyOptionType>({
    label: 'Primary',
    value: Classification.Primary,
  });
  const [significance, setSignificance] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [factDateText, setFactDateText] = useState<string>('');

  const [docSearchQuery, setDocSearchQuery] = useState<string>('');
  const [docComboboxOpen, setDocComboboxOpen] = useState<boolean>(false);
  const debouncedDocSearchQuery = useDebounce(docSearchQuery, 300);
  const { data: docIdsResponse, isLoading: isLoadingDocs } = useGetDocIds(matterId, subsetId, {
    limit: 1000,
    searchQuery: debouncedDocSearchQuery || undefined,
  });

  const { isLoading: isLoadingAddNewFact, mutate } = useCreateFact({
    matterId,
    subsetId,
    docId: docId || selectedDoc?.value || null,
    onSuccess: handleNewFactCreated,
  });

  const handleChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleAddFact = () => {
    if (selectedClassification && selectedRelevance) {
      mutate({
        factDateText,
        classification: selectedClassification.value,
        relevance: selectedRelevance.value,
        sourceText,
        description,
        significance,
        highlightAreas,
      });
    }
  };

  const handleChangeFactDateText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFactDateText(e.target.value);
  };

  const handleChangeSignificance = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSignificance(e.target.value);
  };

  // Use fetched documents if available, otherwise fall back to props
  const availableDocs = docIdsResponse?.documents ?? [];

  const docsOptions = useMemo(
    () =>
      availableDocs.map((doc) => {
        return { value: doc.id, label: doc.file.name };
      }),
    [availableDocs],
  );

  const disabled = (!selectedDoc && !docId) || !description || !factDateText;
  return (
    <>
      {isLoadingAddNewFact ? (
        <div className="flex h-96 flex-col items-center justify-center gap-8">
          <div className="text-xl">Adding Fact...</div>
          <Icon className="lucide--loader-circle animate-spin duration-700" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
            <Icon className="lucide--crop h-5 w-5 flex-shrink-0" />
            <span>
              Facts can also be created automatically by clicking the crop icon (left), on a document, then clicking and
              dragging to select the area of fact source text.
            </span>
          </div>

          {sourceText && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="sourceText">Source Text</Label>
              <Textarea id="sourceText" value={sourceText} placeholder="Source Text" disabled className="h-36" />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="description">Fact Description</Label>
            <Input
              id="description"
              onChange={handleChangeDescription}
              value={description}
              placeholder="Enter fact description"
            />
          </div>

          {!docId && subsetId && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="document">Document</Label>

              <Popover open={docComboboxOpen} onOpenChange={setDocComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={docComboboxOpen}
                    className="w-full justify-between"
                  >
                    {selectedDoc?.label ? (
                      <p className="truncate">{selectedDoc.label}</p>
                    ) : (
                      <p className="text-gray-500">Select a document...</p>
                    )}
                    <Icon className="lucide--chevrons-up-down ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search documents..."
                      value={docSearchQuery}
                      onValueChange={setDocSearchQuery}
                    />
                    <CommandList className="max-h-64 overflow-y-auto">
                      <CommandEmpty>
                        {isLoadingDocs
                          ? 'Loading documents...'
                          : debouncedDocSearchQuery
                            ? `No documents found matching "${debouncedDocSearchQuery}"`
                            : 'No documents found'}
                      </CommandEmpty>
                      <CommandGroup>
                        {docsOptions.map((option) => (
                          <CommandItem
                            key={option.value}
                            className="cursor-pointer hover:bg-gray-100"
                            value={option.label}
                            onSelect={() => {
                              setSelectedDoc({ label: option.label, value: option.value });
                              setDocComboboxOpen(false);
                              setDocSearchQuery('');
                            }}
                          >
                            <Icon
                              className={cn(
                                'lucide--check mr-2 h-4 w-4',
                                selectedDoc?.value === option.value ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="factDate">Date of Fact</Label>
            <Input
              id="factDate"
              onChange={handleChangeFactDateText}
              value={factDateText}
              placeholder="Enter date of fact"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="significance">
              Significance <i className="text-gray-500">(Optional)</i>
            </Label>
            <Input
              id="significance"
              onChange={handleChangeSignificance}
              value={significance}
              placeholder="Enter significance"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex w-1/2 flex-col gap-2">
              <Label htmlFor="relevance">Relevance</Label>

              <Select
                value={selectedRelevance?.value}
                onValueChange={(value) => setSelectedRelevance({ label: value, value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select relevance" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(RelevanceOptions).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex w-1/2 flex-col gap-2">
              <Label htmlFor="classification">Classification</Label>

              <Select
                value={selectedClassification?.value}
                onValueChange={(value) => setSelectedClassification({ label: value, value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select classification" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(ClassificationOptions).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-2">
            <Button onClick={handleAddFact} disabled={disabled}>
              <Icon className="lucide--plus-circle" />
              Add
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
